import React, { useEffect, useState } from 'react';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { esPregunta, recorrido, secciones } from '../../../lib/formularios';
import TarjetaItem from './TarjetaItem';
import { TarjetaAviso } from './Encabezados';
import { mensajeDe } from './utilidades';

/*
 * "Ver puntuación" de quien respondió un cuestionario (`/f/<token>?calificacion=<t>`).
 * Lo que se ve lo decide el servidor según los ajustes del cuestionario: si no manda `correctas`
 * no se pintan las respuestas buenas; si no manda `puntos`, no hay puntos. Aquí solo se pinta
 * lo que llega.
 */

/** La retroalimentación puede venir como texto o como {texto, enlace}. */
export function Retroalimentacion({ retro }) {
  if (!retro) return null;
  const texto = typeof retro === 'string' ? retro : retro.texto;
  const enlace = typeof retro === 'string' ? null : retro.enlace;
  if (!texto && !enlace) return null;
  return (
    <div className="fr-retro">
      <div className="fr-retro-titulo">Comentarios</div>
      {texto && <p className="fr-retro-texto">{texto}</p>}
      {enlace && (
        <a className="fr-retro-enlace" href={enlace} target="_blank" rel="noopener noreferrer nofollow">
          <ExternalLink className="h-4 w-4" aria-hidden="true" /> {enlace}
        </a>
      )}
    </div>
  );
}

/** Insignia "2/2" con palomita o tache, arriba a la derecha de la pregunta. */
export function InsigniaPuntos({ resultado }) {
  if (!resultado) return null;
  const { puntos, maximo, correcta } = resultado;
  const conPuntos = puntos !== undefined && puntos !== null && maximo !== undefined && maximo !== null;
  if (!conPuntos && (correcta === undefined || correcta === null)) return null;
  const clase = correcta === true ? 'is-bien' : correcta === false ? 'is-mal' : '';
  return (
    <span className={`fr-insignia ${clase}`}>
      {correcta === true && <Check className="h-4 w-4" aria-label="Correcta" />}
      {correcta === false && <X className="h-4 w-4" aria-label="Incorrecta" />}
      {conPuntos && <span>{puntos}/{maximo}</span>}
    </span>
  );
}

/** Respuestas correctas de una respuesta corta (en elección se ven marcadas en las opciones). */
function CorrectasDeTexto({ item, resultado }) {
  if (item.tipo !== 'texto_corto' || !Array.isArray(resultado?.correctas) || !resultado.correctas.length) return null;
  if (resultado.correcta === true) return null;
  return (
    <div className="fr-correctas">
      <div className="fr-correctas-titulo">Respuestas correctas</div>
      <ul>{resultado.correctas.map((c) => <li key={c}>{c}</li>)}</ul>
    </div>
  );
}

export default function VistaCalificacion({ datos, token, editarToken }) {
  const [estado, setEstado] = useState({ cargando: true, error: null, datos: null });

  useEffect(() => {
    let vivo = true;
    api.get(`/forms/publico/${token}/calificacion`, { params: { editar: editarToken }, skipAuthRedirect: true })
      .then(({ data }) => vivo && setEstado({ cargando: false, error: null, datos: data }))
      .catch((err) => vivo && setEstado({ cargando: false, error: mensajeDe(err, 'No encontramos esta respuesta.'), datos: null }));
    return () => { vivo = false; };
  }, [token, editarToken]);

  if (estado.cargando) {
    return <div className="fr-cargando"><Loader2 className="h-8 w-8 od-spin" aria-label="Cargando" /></div>;
  }
  if (estado.error) {
    return <TarjetaAviso titulo={datos.titulo}><p>{estado.error}</p></TarjetaAviso>;
  }
  const cal = estado.datos;
  if (!cal.publicada) {
    return (
      <TarjetaAviso titulo={datos.titulo}>
        <p>Tu calificación aún no se publica.</p>
        <p className="fm-texto-2">Cuando quien creó el cuestionario la revise, podrás verla en este mismo enlace.</p>
      </TarjetaAviso>
    );
  }

  const respuestas = cal.respuestas || {};
  const visitadas = recorrido(datos, respuestas);
  const items = secciones(datos.items).filter((s) => visitadas.includes(s.id)).flatMap((s) => s.items);
  const conTotal = cal.total !== undefined && cal.total !== null;

  return (
    <>
      <div className="fm-tarjeta fm-tarjeta-encabezado fr-encabezado">
        <h1 className="fm-titulo-form">{datos.titulo}</h1>
        {conTotal && (
          <div className="fr-puntuacion-total">
            <span className="fr-puntuacion-cifra">{cal.total}</span>
            <span className="fr-puntuacion-de">/{cal.maximo}</span>
            <span className="fr-puntuacion-rotulo">Puntuación total</span>
          </div>
        )}
      </div>
      {items.map((item) => {
        const resultado = cal.preguntas?.[item.id];
        return (
          <TarjetaItem
            key={item.id}
            item={item}
            valor={respuestas[item.id]}
            soloLectura
            marcas={resultado?.correctas ? { correctas: resultado.correctas } : null}
            encabezadoExtra={esPregunta(item) ? <InsigniaPuntos resultado={resultado} /> : null}
            pie={esPregunta(item) ? (
              <>
                <CorrectasDeTexto item={item} resultado={resultado} />
                <Retroalimentacion retro={resultado?.retro} />
              </>
            ) : null}
          />
        );
      })}
    </>
  );
}
